import { PubSubService } from '@ohif/core';
import { AccountRoleEnum, SessionService } from './SessionService';
import { ReportAssessmentService } from './ReportAssessmentService';
import { DiagnosisState, DiagnosisStateContext, describeError, getDiagnosisState } from '../utils';
import {
  DiagnosisStatus,
  fetchDiagnosisStatus,
  revokeReport,
  triggerDiagnosis,
} from '../clients/viewer';
import { UnauthorizedException } from '../http';

const EVENTS = {
  DIAGNOSIS_COMPLETED: 'event::sycai:DiagnosisService:diagnosisCompleted',
  DIAGNOSIS_FAILED: 'event::sycai:DiagnosisService:diagnosisFailed',
  DIAGNOSIS_STATE_CHANGED: 'event::sycai:DiagnosisService:diagnosisStateChanged',
  SESSION_LOST: 'event::sycai:DiagnosisService:sessionLost',
} as const;

export type DiagnosisEventPayloads = {
  [EVENTS.DIAGNOSIS_COMPLETED]: { studyInstanceUid: string };
  [EVENTS.DIAGNOSIS_FAILED]: { studyInstanceUid: string };
  [EVENTS.DIAGNOSIS_STATE_CHANGED]: { studyInstanceUid: string; state: DiagnosisState };
  [EVENTS.SESSION_LOST]: { studyInstanceUid: string };
};

const INITIAL_CONTEXT: DiagnosisStateContext = {
  role: null,
  isGenerating: false,
  isReportGenerated: false,
  assessmentRequired: false,
};

export class DiagnosisService extends PubSubService {
  public static readonly EVENTS = EVENTS;

  public static readonly REGISTRATION = {
    name: 'diagnosisService',
    create: ({ servicesManager }: { servicesManager: AppTypes.ServicesManager }) =>
      new DiagnosisService(servicesManager),
  };

  private emit<E extends keyof DiagnosisEventPayloads>(
    event: E,
    payload: DiagnosisEventPayloads[E]
  ): void {
    this._broadcastEvent(event, payload);
  }

  private readonly sessionService: SessionService;
  private readonly reportAssessmentService: ReportAssessmentService;

  private studies = new Map<string, { context: DiagnosisStateContext; state: DiagnosisState }>();

  private runsInFlight = new Set<string>();

  private generations = new Map<string, number>();

  private refreshesInFlight = new Map<string, { generation: number; request: Promise<void> }>();

  private trackedStudyInstanceUid: string | null = null;

  constructor(servicesManager: AppTypes.ServicesManager) {
    super(EVENTS);

    const { sessionService, reportAssessmentService } = servicesManager.services;

    if (!sessionService || !reportAssessmentService) {
      throw new Error(
        '[sycai] DiagnosisService was constructed before sessionService and ' +
          'reportAssessmentService were registered'
      );
    }

    this.sessionService = sessionService;
    this.reportAssessmentService = reportAssessmentService;
  }

  public getState(studyInstanceUid: string): DiagnosisState {
    return this.studies.get(studyInstanceUid)?.state ?? DiagnosisState.IDLE;
  }

  public getTrackedStudy(): string | null {
    return this.trackedStudyInstanceUid;
  }

  public async track(studyInstanceUid: string): Promise<void> {
    this.trackedStudyInstanceUid = studyInstanceUid;
    await this.refresh(studyInstanceUid);
  }

  public async refresh(studyInstanceUid: string): Promise<void> {
    const generation = this.generationOf(studyInstanceUid);
    const inFlight = this.refreshesInFlight.get(studyInstanceUid);

    if (inFlight && inFlight.generation === generation) {
      return inFlight.request;
    }

    const request = this.read(studyInstanceUid, generation).finally(() => {
      // Only if the entry is still this request's. A newer generation may have replaced
      // it while this one was out, and deleting that one would let the next caller issue
      // a duplicate against a request that is still live.
      if (this.refreshesInFlight.get(studyInstanceUid)?.request === request) {
        this.refreshesInFlight.delete(studyInstanceUid);
      }
    });

    this.refreshesInFlight.set(studyInstanceUid, { generation, request });

    return request;
  }

  private async read(studyInstanceUid: string, generation: number): Promise<void> {
    try {
      const status = await fetchDiagnosisStatus(studyInstanceUid);

      if (this.isStale(studyInstanceUid, generation)) {
        return;
      }

      if (status === DiagnosisStatus.IN_PROGRESS) {
        this.runsInFlight.add(studyInstanceUid);
        this.update(studyInstanceUid, { isGenerating: true });
        return;
      }

      const resolution = await this.readReport(studyInstanceUid, status);

      if (this.isStale(studyInstanceUid, generation)) {
        return;
      }

      // State FIRST, then the completion event, so a subscriber that reads the service
      // sees the resolved state rather than the one it just left.
      this.update(studyInstanceUid, { isGenerating: false, ...resolution });

      if (!this.runsInFlight.delete(studyInstanceUid)) {
        return;
      }

      if (status === DiagnosisStatus.COMPLETED) {
        this.emit(EVENTS.DIAGNOSIS_COMPLETED, { studyInstanceUid });
      }

      if (status === DiagnosisStatus.FAILED) {
        this.emit(EVENTS.DIAGNOSIS_FAILED, { studyInstanceUid });
      }
    } catch (error) {
      this.report(studyInstanceUid, 'reading the diagnosis status', error);
    }
  }

  private async readReport(
    studyInstanceUid: string,
    status: DiagnosisStatus
  ): Promise<Partial<DiagnosisStateContext>> {
    if (status === DiagnosisStatus.FAILED) {
      return { isReportGenerated: false, assessmentRequired: false };
    }

    try {
      const { isReportGenerated, assessmentRequired } =
        await this.reportAssessmentService.refreshStudy(studyInstanceUid);

      return { isReportGenerated, assessmentRequired };
    } catch (error) {
      this.report(studyInstanceUid, 'reading the report assessments', error);
      return {};
    }
  }

  public async trigger(studyInstanceUid: string): Promise<void> {
    this.bumpGeneration(studyInstanceUid);
    this.update(studyInstanceUid, { isGenerating: true });

    try {
      await triggerDiagnosis(studyInstanceUid);
    } catch (error) {
      this.report(studyInstanceUid, 'triggering the diagnosis', error);
      return;
    }

    this.bumpGeneration(studyInstanceUid);
    this.runsInFlight.add(studyInstanceUid);
    this.update(studyInstanceUid, { isGenerating: true });
  }

  public async revoke(studyInstanceUid: string): Promise<void> {
    if (this.sessionService.getRole() !== AccountRoleEnum.ENVIRONMENT_ADMIN) {
      return;
    }

    this.bumpGeneration(studyInstanceUid);

    try {
      await revokeReport(studyInstanceUid);
    } catch (error) {
      this.report(studyInstanceUid, 'revoking the report', error);
      return;
    }

    this.bumpGeneration(studyInstanceUid);

    await this.refresh(studyInstanceUid);
  }

  public async submitAssessment(studyInstanceUid: string): Promise<void> {
    this.bumpGeneration(studyInstanceUid);
    this.update(studyInstanceUid, { isGenerating: true });

    try {
      await this.reportAssessmentService.submitStudy(studyInstanceUid);
    } catch (error) {
      this.report(studyInstanceUid, 'submitting the assessment', error);
    }

    // Bumped whether or not the submit threw. On success it is what makes the read-back
    // below a fresh request rather than a tick issued before the submit landed; on
    // failure it costs one extra round trip and never lies.
    this.bumpGeneration(studyInstanceUid);

    await this.refresh(studyInstanceUid);
  }

  public onModeExit(): void {
    const touched = new Set([
      ...this.studies.keys(),
      ...this.refreshesInFlight.keys(),
      ...this.generations.keys(),
    ]);

    touched.forEach(studyInstanceUid => this.bumpGeneration(studyInstanceUid));

    this.studies.clear();
    this.runsInFlight.clear();

    this.refreshesInFlight.clear();

    this.trackedStudyInstanceUid = null;
  }

  private generationOf(studyInstanceUid: string): number {
    return this.generations.get(studyInstanceUid) ?? 0;
  }

  private bumpGeneration(studyInstanceUid: string): void {
    this.generations.set(studyInstanceUid, this.generationOf(studyInstanceUid) + 1);
  }

  /** True once anything has superseded the generation this response was issued under. */
  private isStale(studyInstanceUid: string, generation: number): boolean {
    return this.generationOf(studyInstanceUid) !== generation;
  }

  private update(studyInstanceUid: string, patch: Partial<DiagnosisStateContext>): void {
    const previous = this.studies.get(studyInstanceUid);

    const context: DiagnosisStateContext = {
      ...INITIAL_CONTEXT,
      ...previous?.context,
      ...patch,
      role: this.sessionService.getRole(),
    };

    const state = getDiagnosisState(context);
    this.studies.set(studyInstanceUid, { context, state });

    if (previous?.state === state) {
      return;
    }

    // The payload carries the UID and the state, never an account or a token.
    this.emit(EVENTS.DIAGNOSIS_STATE_CHANGED, { studyInstanceUid, state });
  }

  private report(studyInstanceUid: string, action: string, error: unknown): void {
    console.error(
      `[sycai] ${action} failed ${describeError(error)}; the button keeps its last state`
    );

    if (error instanceof UnauthorizedException) {
      this.emit(EVENTS.SESSION_LOST, { studyInstanceUid });
    }
  }
}

export interface DiagnosisService {
  subscribe: (<E extends keyof DiagnosisEventPayloads>(
    eventName: E,
    callback: (data: DiagnosisEventPayloads[E]) => void
  ) => { unsubscribe: () => void }) &
    PubSubService['subscribe'];
}
